import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Briefcase, CheckCircle2, Users, FileCheck, ClipboardList, Send, Loader2, Building2, Scale, Plus, X, ArrowLeft, ArrowRight, Pencil } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const expectationIcons = [Users, ClipboardList, FileCheck, Scale];

const initialForm = {
  fullName: '', email: '', phone: '', statesLicensed: '', npn: '',
  yearsExperience: '', appointedWithACA: '', marketplaceExperience: '', highVolumeExperience: '',
  productExperience: [], productExperienceOther: '',
  currentlyAffiliated: '', affiliations: [],
  carrierRelationshipsStatus: '', carrierRelationships: [],
  managesBook: '', bookSize: '', bookPrimaryLines: [],
  experienceDescription: ''
};

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const TOTAL_STEPS = 7;
const stepKeys = ['stepPersonal', 'stepLicensing', 'stepExperience', 'stepAffiliations', 'stepCarriers', 'stepBook', 'stepReview'];

export const WorkWithUsPage = () => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [step, setStep] = useState(1);
  const formTopRef = useRef(null);

  const expectations = t('workWithUs.expectations', { returnObjects: true });
  const requirements = t('workWithUs.requirements', { returnObjects: true });
  const experienceYears = t('workWithUs.experienceYears', { returnObjects: true });
  const yesNoOptions = [
    { value: 'yes', label: t('workWithUs.yes') },
    { value: 'no', label: t('workWithUs.no') }
  ];
  const yesNoNotSure = [
    { value: 'yes', label: t('workWithUs.yes') },
    { value: 'no', label: t('workWithUs.no') },
    { value: 'not-sure', label: t('workWithUs.notSure') }
  ];
  const productOptions = t('workWithUs.productOptions', { returnObjects: true });
  const relationshipTypeOptions = t('workWithUs.relationshipTypeOptions', { returnObjects: true });
  const carrierStatusOptions = t('workWithUs.carrierStatusOptions', { returnObjects: true });
  const bookSizeOptions = t('workWithUs.bookSizeOptions', { returnObjects: true });

  const labelFor = (options, value) => (options.find((o) => o.value === value) || {}).label || value;

  const toggleArrayVal = (field, value) => {
    setFormData((prev) => {
      const arr = prev[field] || [];
      return { ...prev, [field]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });
  };

  const emptyAffiliation = { organizationName: '', relationshipType: '', relationshipTypeOther: '' };
  const addAffiliation = () => setFormData((p) => ({ ...p, affiliations: [...p.affiliations, { ...emptyAffiliation }] }));
  const updateAffiliation = (i, key, val) => setFormData((p) => ({ ...p, affiliations: p.affiliations.map((a, idx) => (idx === i ? { ...a, [key]: val } : a)) }));
  const removeAffiliation = (i) => setFormData((p) => ({ ...p, affiliations: p.affiliations.filter((_, idx) => idx !== i) }));

  const emptyCarrier = { carrierName: '', productLine: '', states: '', status: '', statusOther: '', hasBook: '', activeClientsCount: '', bookWithOtherOrg: '', bookOrgName: '' };
  const addCarrier = () => setFormData((p) => ({ ...p, carrierRelationships: [...p.carrierRelationships, { ...emptyCarrier }] }));
  const updateCarrier = (i, key, val) => setFormData((p) => ({ ...p, carrierRelationships: p.carrierRelationships.map((c, idx) => (idx === i ? { ...c, [key]: val } : c)) }));
  const removeCarrier = (i) => setFormData((p) => ({ ...p, carrierRelationships: p.carrierRelationships.filter((_, idx) => idx !== i) }));

  const setAffiliated = (v) => setFormData((p) => ({ ...p, currentlyAffiliated: v, affiliations: v === 'yes' && p.affiliations.length === 0 ? [{ ...emptyAffiliation }] : p.affiliations }));
  const setCarrierStatus = (v) => setFormData((p) => ({ ...p, carrierRelationshipsStatus: v, carrierRelationships: v === 'yes' && p.carrierRelationships.length === 0 ? [{ ...emptyCarrier }] : p.carrierRelationships }));

  const scrollToTop = () => { if (formTopRef.current) formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  const validateStep = (s) => {
    if (s === 1) return !!(formData.fullName && formData.email);
    if (s === 2) return !!(formData.statesLicensed && formData.npn);
    return true;
  };

  const goNext = () => {
    if (!validateStep(step)) { toast.error(t('workWithUs.errRequired')); return; }
    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
    scrollToTop();
  };
  const goBack = () => { setStep((s) => Math.max(s - 1, 1)); scrollToTop(); };
  const goToStep = (s) => { setStep(s); scrollToTop(); };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!formData.fullName || !formData.email || !formData.npn || !formData.statesLicensed) {
      toast.error(t('workWithUs.errRequired'));
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/agent-interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Failed to submit form');
      setIsSubmitted(true);
      toast.success(t('workWithUs.successToast'));
    } catch (error) {
      toast.error(t('workWithUs.errorToast'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center py-20" data-testid="agent-success">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-lg mx-auto text-center">
            <div className="h-20 w-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">{t('workWithUs.thankYou')}</h2>
            <p className="text-slate-600 mb-8">{t('workWithUs.thankYouMsg')}</p>
            <Button onClick={() => { setIsSubmitted(false); setFormData(initialForm); setStep(1); }} variant="outline" className="rounded-full" data-testid="submit-another-btn">
              {t('workWithUs.submitAnother')}
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  const inputCls = 'h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500';

  const ProductChips = ({ field, testPrefix }) => (
    <div className="flex flex-wrap gap-2">
      {productOptions.map((opt) => {
        const active = formData[field].includes(opt.value);
        return (
          <button type="button" key={opt.value} onClick={() => toggleArrayVal(field, opt.value)}
            className={`px-3 py-2 rounded-full text-sm border transition-colors ${active ? 'bg-sky-500 text-white border-sky-500' : 'bg-white text-slate-600 border-slate-200 hover:border-sky-300'}`}
            data-testid={`${testPrefix}-${slug(opt.value)}`}>
            {opt.label}
          </button>
        );
      })}
    </div>
  );

  const ReviewSection = ({ title, stepNo, testId, children }) => (
    <div className="border border-slate-200 rounded-xl p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-slate-900">{title}</h4>
        <button type="button" onClick={() => goToStep(stepNo)} className="text-sky-600 text-sm font-medium inline-flex items-center gap-1 hover:text-sky-700" data-testid={`edit-step-${stepNo}`}>
          <Pencil className="h-3.5 w-3.5" />{t('workWithUs.edit')}
        </button>
      </div>
      <div className="space-y-1.5 text-sm">{children}</div>
    </div>
  );

  const Row = ({ label, value }) => (
    <div className="flex flex-col sm:flex-row sm:gap-3">
      <span className="text-slate-500 sm:w-56 sm:flex-shrink-0">{label}</span>
      <span className="text-slate-900 font-medium break-words">{value || t('workWithUs.notProvided')}</span>
    </div>
  );

  const progressPct = Math.round((step / TOTAL_STEPS) * 100);

  return (
    <div className="overflow-hidden" data-testid="work-with-us-page">
      <section className="bg-slate-900 py-20 lg:py-28" data-testid="wwu-hero">
        <div className="container-custom">
          <motion.div initial="initial" animate="animate" variants={fadeInUp} className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500/20 text-sky-400 rounded-full text-sm font-medium mb-6">
              <Briefcase className="h-4 w-4" />
              {t('workWithUs.heroBadge')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-5xl font-bold text-white tracking-tight leading-tight mb-6">
              {t('workWithUs.heroTitlePre')}{' '}<span className="text-sky-400">{t('workWithUs.heroHighlight')}</span>
            </h1>
            <p className="text-lg text-slate-300 mb-8 max-w-3xl mx-auto">{t('workWithUs.heroDesc')}</p>
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-white" data-testid="wwu-about">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{t('workWithUs.aboutTitle')}</h2>
              <div className="text-lg text-slate-600 space-y-4 text-left">
                <p>{t('workWithUs.aboutP1')}</p>
                <p>{t('workWithUs.aboutP2')}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-spacing gradient-subtle" data-testid="wwu-expectations">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('workWithUs.expectTitle')}</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {expectations.map((item, idx) => {
              const Icon = expectationIcons[idx] || Users;
              return (
                <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white p-6 rounded-2xl border border-slate-100 card-shadow text-center">
                  <div className="h-12 w-12 bg-sky-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-6 w-6 text-sky-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-spacing bg-white" data-testid="wwu-requirements">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{t('workWithUs.reqTitle')}</h2>
                <ul className="space-y-4">
                  {requirements.map((requirement) => (
                    <li key={requirement} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-sky-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{requirement}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
              <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-slate-900 rounded-2xl p-8 text-white">
                <Building2 className="h-12 w-12 text-sky-400 mb-6" />
                <h3 className="text-xl font-bold mb-4">{t('workWithUs.whoTitle')}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{t('workWithUs.whoText')}</p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-spacing gradient-subtle" data-testid="wwu-form">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto" ref={formTopRef}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('workWithUs.formTitle')}</h2>
              <p className="text-lg text-slate-600">{t('workWithUs.formSubtitle')}</p>
            </motion.div>

            <div className="bg-white rounded-2xl p-6 sm:p-8 card-shadow">
              {/* Progress */}
              <div className="mb-8" data-testid="wizard-progress">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-sky-600" data-testid="step-indicator">{t('workWithUs.stepOf', { current: step, total: TOTAL_STEPS })}</span>
                  <span className="text-sm font-medium text-slate-700">{t(`workWithUs.${stepKeys[step - 1]}`)}</span>
                </div>
                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-sky-500 rounded-full transition-all duration-300" style={{ width: `${progressPct}%` }} />
                </div>
              </div>

              <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
                {/* STEP 1 — Personal Information */}
                {step === 1 && (
                  <div className="space-y-6" data-testid="step-1">
                    <div className="space-y-2">
                      <Label htmlFor="fullName" className="text-slate-700 font-medium">{t('workWithUs.fullName')} <span className="text-red-500">*</span></Label>
                      <Input id="fullName" type="text" placeholder={t('workWithUs.namePlaceholder')} value={formData.fullName} onChange={(e) => setFormData({ ...formData, fullName: e.target.value })} className={inputCls} data-testid="input-fullname" />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-700 font-medium">{t('workWithUs.email')} <span className="text-red-500">*</span></Label>
                        <Input id="email" type="email" placeholder={t('workWithUs.emailPlaceholder')} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={inputCls} data-testid="input-email" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-slate-700 font-medium">{t('workWithUs.phone')}</Label>
                        <Input id="phone" type="tel" placeholder={t('workWithUs.phonePlaceholder')} value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className={inputCls} data-testid="input-phone" />
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 2 — Licensing */}
                {step === 2 && (
                  <div className="space-y-6" data-testid="step-2">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="statesLicensed" className="text-slate-700 font-medium">{t('workWithUs.statesLicensed')} <span className="text-red-500">*</span></Label>
                        <Input id="statesLicensed" type="text" placeholder={t('workWithUs.statesPlaceholder')} value={formData.statesLicensed} onChange={(e) => setFormData({ ...formData, statesLicensed: e.target.value })} className={inputCls} data-testid="input-states" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="npn" className="text-slate-700 font-medium">{t('workWithUs.npn')} <span className="text-red-500">*</span></Label>
                        <Input id="npn" type="text" placeholder={t('workWithUs.npnPlaceholder')} value={formData.npn} onChange={(e) => setFormData({ ...formData, npn: e.target.value })} className={inputCls} data-testid="input-npn" />
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 3 — Insurance Experience */}
                {step === 3 && (
                  <div className="space-y-6" data-testid="step-3">
                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium">{t('workWithUs.yearsExperienceLabel')}</Label>
                      <Select value={formData.yearsExperience} onValueChange={(value) => setFormData({ ...formData, yearsExperience: value })}>
                        <SelectTrigger className="h-12 rounded-xl border-slate-200" data-testid="select-experience"><SelectValue placeholder={t('workWithUs.selectExperience')} /></SelectTrigger>
                        <SelectContent>
                          {experienceYears.map((option) => (<SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-3" data-testid="wwu-product-experience">
                      <Label className="text-slate-700 font-medium">{t('workWithUs.productExperienceLabel')}</Label>
                      <p className="text-xs text-slate-500">{t('workWithUs.productExperienceHint')}</p>
                      <ProductChips field="productExperience" testPrefix="product-chip" />
                      {formData.productExperience.includes('Other') && (
                        <Input type="text" placeholder={t('workWithUs.productOtherPlaceholder')} value={formData.productExperienceOther} onChange={(e) => setFormData({ ...formData, productExperienceOther: e.target.value })} className={inputCls} data-testid="input-product-other" />
                      )}
                    </div>
                    <div className="space-y-4 pt-2 border-t border-slate-100">
                      <div className="space-y-2">
                        <Label className="text-slate-700 font-medium">{t('workWithUs.qAppointed')}</Label>
                        <Select value={formData.appointedWithACA} onValueChange={(value) => setFormData({ ...formData, appointedWithACA: value })}>
                          <SelectTrigger className="h-12 rounded-xl border-slate-200" data-testid="select-appointed"><SelectValue placeholder={t('workWithUs.selectOption')} /></SelectTrigger>
                          <SelectContent>{yesNoOptions.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-700 font-medium">{t('workWithUs.qMarketplace')}</Label>
                        <Select value={formData.marketplaceExperience} onValueChange={(value) => setFormData({ ...formData, marketplaceExperience: value })}>
                          <SelectTrigger className="h-12 rounded-xl border-slate-200" data-testid="select-marketplace"><SelectValue placeholder={t('workWithUs.selectOption')} /></SelectTrigger>
                          <SelectContent>{yesNoOptions.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-700 font-medium">{t('workWithUs.qHighVolume')}</Label>
                        <Select value={formData.highVolumeExperience} onValueChange={(value) => setFormData({ ...formData, highVolumeExperience: value })}>
                          <SelectTrigger className="h-12 rounded-xl border-slate-200" data-testid="select-highvolume"><SelectValue placeholder={t('workWithUs.selectOption')} /></SelectTrigger>
                          <SelectContent>{yesNoOptions.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 4 — Current Affiliations */}
                {step === 4 && (
                  <div className="space-y-4" data-testid="step-4">
                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium">{t('workWithUs.affiliatedLabel')}</Label>
                      <Select value={formData.currentlyAffiliated} onValueChange={setAffiliated}>
                        <SelectTrigger className="h-12 rounded-xl border-slate-200" data-testid="select-affiliated"><SelectValue placeholder={t('workWithUs.selectOption')} /></SelectTrigger>
                        <SelectContent>{yesNoOptions.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    {formData.currentlyAffiliated === 'yes' && (
                      <div className="space-y-4" data-testid="affiliations-list">
                        {formData.affiliations.map((aff, i) => (
                          <div key={i} className="border border-slate-200 rounded-xl p-4 space-y-3" data-testid={`affiliation-${i}`}>
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-slate-500">#{i + 1}</span>
                              <button type="button" onClick={() => removeAffiliation(i)} className="text-slate-400 hover:text-red-500" data-testid={`remove-affiliation-${i}`}><X className="h-4 w-4" /></button>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-slate-600 text-sm">{t('workWithUs.orgNameLabel')}</Label>
                              <Input type="text" placeholder={t('workWithUs.orgNamePlaceholder')} value={aff.organizationName} onChange={(e) => updateAffiliation(i, 'organizationName', e.target.value)} className="h-11 rounded-xl border-slate-200" data-testid={`affiliation-org-${i}`} />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-slate-600 text-sm">{t('workWithUs.relationshipTypeLabel')}</Label>
                              <Select value={aff.relationshipType} onValueChange={(v) => updateAffiliation(i, 'relationshipType', v)}>
                                <SelectTrigger className="h-11 rounded-xl border-slate-200" data-testid={`affiliation-reltype-${i}`}><SelectValue placeholder={t('workWithUs.selectOption')} /></SelectTrigger>
                                <SelectContent>{relationshipTypeOptions.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
                              </Select>
                            </div>
                            {aff.relationshipType === 'Other' && (
                              <Input type="text" placeholder={t('workWithUs.relationshipOtherPlaceholder')} value={aff.relationshipTypeOther} onChange={(e) => updateAffiliation(i, 'relationshipTypeOther', e.target.value)} className="h-11 rounded-xl border-slate-200" data-testid={`affiliation-relother-${i}`} />
                            )}
                          </div>
                        ))}
                        <Button type="button" variant="outline" onClick={addAffiliation} className="rounded-full" data-testid="add-affiliation-btn"><Plus className="h-4 w-4 mr-2" />{t('workWithUs.addOrganization')}</Button>
                      </div>
                    )}
                  </div>
                )}

                {/* STEP 5 — Carrier Relationships */}
                {step === 5 && (
                  <div className="space-y-4" data-testid="step-5">
                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium">{t('workWithUs.carrierStatusQLabel')}</Label>
                      <Select value={formData.carrierRelationshipsStatus} onValueChange={setCarrierStatus}>
                        <SelectTrigger className="h-12 rounded-xl border-slate-200" data-testid="select-carrier-status"><SelectValue placeholder={t('workWithUs.selectOption')} /></SelectTrigger>
                        <SelectContent>{yesNoOptions.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    {formData.carrierRelationshipsStatus === 'yes' && (
                      <div className="space-y-4" data-testid="carriers-list">
                        {formData.carrierRelationships.map((c, i) => (
                          <div key={i} className="border border-slate-200 rounded-xl p-4 space-y-3" data-testid={`carrier-${i}`}>
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-slate-500">{t('workWithUs.carrierNameLabel')} #{i + 1}</span>
                              <button type="button" onClick={() => removeCarrier(i)} className="text-slate-400 hover:text-red-500" data-testid={`remove-carrier-${i}`}><X className="h-4 w-4" /></button>
                            </div>
                            <Input type="text" placeholder={t('workWithUs.carrierNamePlaceholder')} value={c.carrierName} onChange={(e) => updateCarrier(i, 'carrierName', e.target.value)} className="h-11 rounded-xl border-slate-200" data-testid={`carrier-name-${i}`} />
                            <div className="grid md:grid-cols-2 gap-3">
                              <Input type="text" placeholder={t('workWithUs.productLinePlaceholder')} value={c.productLine} onChange={(e) => updateCarrier(i, 'productLine', e.target.value)} className="h-11 rounded-xl border-slate-200" data-testid={`carrier-product-${i}`} />
                              <Input type="text" placeholder={t('workWithUs.carrierStatesPlaceholder')} value={c.states} onChange={(e) => updateCarrier(i, 'states', e.target.value)} className="h-11 rounded-xl border-slate-200" data-testid={`carrier-states-${i}`} />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-slate-600 text-sm">{t('workWithUs.carrierStatusLabel')}</Label>
                              <Select value={c.status} onValueChange={(v) => updateCarrier(i, 'status', v)}>
                                <SelectTrigger className="h-11 rounded-xl border-slate-200" data-testid={`carrier-statussel-${i}`}><SelectValue placeholder={t('workWithUs.selectOption')} /></SelectTrigger>
                                <SelectContent>{carrierStatusOptions.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
                              </Select>
                            </div>
                            {c.status === 'Other' && (
                              <Input type="text" placeholder={t('workWithUs.carrierStatusOtherPlaceholder')} value={c.statusOther} onChange={(e) => updateCarrier(i, 'statusOther', e.target.value)} className="h-11 rounded-xl border-slate-200" data-testid={`carrier-statusother-${i}`} />
                            )}
                            <div className="space-y-2 pt-1">
                              <Label className="text-slate-600 text-sm">{t('workWithUs.carrierBookQLabel')}</Label>
                              <Select value={c.hasBook} onValueChange={(v) => updateCarrier(i, 'hasBook', v)}>
                                <SelectTrigger className="h-11 rounded-xl border-slate-200" data-testid={`carrier-hasbook-${i}`}><SelectValue placeholder={t('workWithUs.selectOption')} /></SelectTrigger>
                                <SelectContent>{yesNoOptions.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
                              </Select>
                            </div>
                            {c.hasBook === 'yes' && (
                              <div className="space-y-3 pl-3 border-l-2 border-sky-100">
                                <div className="space-y-2">
                                  <Label className="text-slate-600 text-sm">{t('workWithUs.activeClientsLabel')}</Label>
                                  <Input type="number" min="0" placeholder={t('workWithUs.activeClientsPlaceholder')} value={c.activeClientsCount} onChange={(e) => updateCarrier(i, 'activeClientsCount', e.target.value)} className="h-11 rounded-xl border-slate-200" data-testid={`carrier-clients-${i}`} />
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-slate-600 text-sm">{t('workWithUs.bookWithOrgLabel')}</Label>
                                  <Select value={c.bookWithOtherOrg} onValueChange={(v) => updateCarrier(i, 'bookWithOtherOrg', v)}>
                                    <SelectTrigger className="h-11 rounded-xl border-slate-200" data-testid={`carrier-bookorg-${i}`}><SelectValue placeholder={t('workWithUs.selectOption')} /></SelectTrigger>
                                    <SelectContent>{yesNoNotSure.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
                                  </Select>
                                </div>
                                {c.bookWithOtherOrg === 'yes' && (
                                  <Input type="text" placeholder={t('workWithUs.orgNamePlaceholder')} value={c.bookOrgName} onChange={(e) => updateCarrier(i, 'bookOrgName', e.target.value)} className="h-11 rounded-xl border-slate-200" data-testid={`carrier-bookorgname-${i}`} />
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                        <Button type="button" variant="outline" onClick={addCarrier} className="rounded-full" data-testid="add-carrier-btn"><Plus className="h-4 w-4 mr-2" />{t('workWithUs.addCarrier')}</Button>
                      </div>
                    )}
                  </div>
                )}

                {/* STEP 6 — Book of Business */}
                {step === 6 && (
                  <div className="space-y-4" data-testid="step-6">
                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium">{t('workWithUs.managesBookLabel')}</Label>
                      <Select value={formData.managesBook} onValueChange={(v) => setFormData({ ...formData, managesBook: v })}>
                        <SelectTrigger className="h-12 rounded-xl border-slate-200" data-testid="select-manages-book"><SelectValue placeholder={t('workWithUs.selectOption')} /></SelectTrigger>
                        <SelectContent>{yesNoOptions.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    {formData.managesBook === 'yes' && (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-slate-700 font-medium">{t('workWithUs.bookSizeLabel')}</Label>
                          <Select value={formData.bookSize} onValueChange={(v) => setFormData({ ...formData, bookSize: v })}>
                            <SelectTrigger className="h-12 rounded-xl border-slate-200" data-testid="select-book-size"><SelectValue placeholder={t('workWithUs.selectOption')} /></SelectTrigger>
                            <SelectContent>{bookSizeOptions.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2" data-testid="book-primary-lines">
                          <Label className="text-slate-700 font-medium">{t('workWithUs.bookLinesLabel')}</Label>
                          <ProductChips field="bookPrimaryLines" testPrefix="bookline-chip" />
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-slate-500 mt-4 leading-relaxed">{t('workWithUs.bookPrivacyNote')}</p>
                  </div>
                )}

                {/* STEP 7 — Review & Submit */}
                {step === 7 && (
                  <div className="space-y-5" data-testid="step-7">
                    <div className="text-center mb-2">
                      <h3 className="text-xl font-bold text-slate-900">{t('workWithUs.reviewTitle')}</h3>
                      <p className="text-sm text-slate-500 mt-1">{t('workWithUs.reviewSubtitle')}</p>
                    </div>

                    <ReviewSection title={t('workWithUs.reviewPersonal')} stepNo={1} testId="review-personal">
                      <Row label={t('workWithUs.fullName')} value={formData.fullName} />
                      <Row label={t('workWithUs.email')} value={formData.email} />
                      <Row label={t('workWithUs.phone')} value={formData.phone} />
                    </ReviewSection>

                    <ReviewSection title={t('workWithUs.reviewLicensing')} stepNo={2} testId="review-licensing">
                      <Row label={t('workWithUs.statesLicensed')} value={formData.statesLicensed} />
                      <Row label={t('workWithUs.npn')} value={formData.npn} />
                    </ReviewSection>

                    <ReviewSection title={t('workWithUs.reviewExperience')} stepNo={3} testId="review-experience">
                      <Row label={t('workWithUs.yearsExperienceLabel')} value={formData.yearsExperience ? labelFor(experienceYears, formData.yearsExperience) : ''} />
                      <Row label={t('workWithUs.productExperienceLabel')} value={formData.productExperience.length ? formData.productExperience.map((v) => labelFor(productOptions, v)).join(', ') : t('workWithUs.productsNone')} />
                      {formData.productExperience.includes('Other') && <Row label="Other" value={formData.productExperienceOther} />}
                    </ReviewSection>

                    <ReviewSection title={t('workWithUs.reviewAffiliations')} stepNo={4} testId="review-affiliations">
                      {formData.currentlyAffiliated === 'yes' && formData.affiliations.length ? (
                        formData.affiliations.map((a, i) => (
                          <Row key={i} label={`${t('workWithUs.orgNameLabel')} #${i + 1}`} value={`${a.organizationName || t('workWithUs.notProvided')} — ${a.relationshipType === 'Other' ? a.relationshipTypeOther : (a.relationshipType ? labelFor(relationshipTypeOptions, a.relationshipType) : t('workWithUs.notProvided'))}`} />
                        ))
                      ) : (
                        <span className="text-slate-500">{t('workWithUs.affiliationsNone')}</span>
                      )}
                    </ReviewSection>

                    <ReviewSection title={t('workWithUs.reviewCarriers')} stepNo={5} testId="review-carriers">
                      {formData.carrierRelationshipsStatus === 'yes' && formData.carrierRelationships.length ? (
                        formData.carrierRelationships.map((c, i) => (
                          <div key={i} className="pb-1">
                            <Row label={`${t('workWithUs.carrierNameLabel')} #${i + 1}`} value={`${c.carrierName || t('workWithUs.notProvided')} · ${c.productLine || '—'} · ${c.states || '—'} · ${c.status === 'Other' ? c.statusOther : (c.status ? labelFor(carrierStatusOptions, c.status) : '—')}`} />
                            {c.hasBook === 'yes' && <Row label={t('workWithUs.activeClientsLabel')} value={`~${c.activeClientsCount || '—'}${c.bookWithOtherOrg === 'yes' && c.bookOrgName ? ` · ${c.bookOrgName}` : ''}`} />}
                          </div>
                        ))
                      ) : (
                        <span className="text-slate-500">{t('workWithUs.carriersNone')}</span>
                      )}
                    </ReviewSection>

                    <ReviewSection title={t('workWithUs.reviewBook')} stepNo={6} testId="review-book">
                      {formData.managesBook === 'yes' ? (
                        <>
                          <Row label={t('workWithUs.bookSizeLabel')} value={formData.bookSize ? labelFor(bookSizeOptions, formData.bookSize) : ''} />
                          <Row label={t('workWithUs.bookLinesLabel')} value={formData.bookPrimaryLines.length ? formData.bookPrimaryLines.map((v) => labelFor(productOptions, v)).join(', ') : ''} />
                        </>
                      ) : (
                        <span className="text-slate-500">{t('workWithUs.bookNone')}</span>
                      )}
                    </ReviewSection>

                    <div className="space-y-2">
                      <Label htmlFor="experienceDescription" className="text-slate-700 font-medium">{t('workWithUs.descLabel')}</Label>
                      <Textarea id="experienceDescription" placeholder={t('workWithUs.descPlaceholder')} value={formData.experienceDescription} onChange={(e) => setFormData({ ...formData, experienceDescription: e.target.value })} className="min-h-[110px] rounded-xl border-slate-200 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 resize-none" data-testid="input-description" />
                    </div>
                    <p className="text-center text-xs text-slate-500 leading-relaxed">{t('workWithUs.formDisclaimer')}</p>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex items-center justify-between gap-3 pt-4 border-t border-slate-100">
                  {step > 1 ? (
                    <Button type="button" variant="outline" onClick={goBack} className="rounded-full" data-testid="wizard-back-btn"><ArrowLeft className="h-4 w-4 mr-2" />{t('workWithUs.back')}</Button>
                  ) : <span />}
                  {step < TOTAL_STEPS ? (
                    <Button type="button" onClick={goNext} className="btn-primary" data-testid="wizard-next-btn">{t('workWithUs.next')}<ArrowRight className="h-4 w-4 ml-2" /></Button>
                  ) : (
                    <Button type="button" onClick={handleSubmit} disabled={isSubmitting} className="btn-primary" data-testid="submit-agent-btn">
                      {isSubmitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('workWithUs.submitting')}</>) : (<>{t('workWithUs.submit')}<Send className="ml-2 h-4 w-4" /></>)}
                    </Button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WorkWithUsPage;
